import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fmtDate, inr, inrFull, num } from "../../lib/format";
import { Empty, SectionTitle } from "../kit";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export const MTO_ST = {
  open: ["Open", "bg-sky-50 text-sky-700 border-sky-200"],
  partially_closed: ["Partially Closed", "bg-amber-50 text-amber-800 border-amber-200"],
  completed: ["Completed / Closed", "bg-green-50 text-green-700 border-green-200"],
  cancelled: ["Cancelled", "bg-red-50 text-red-700 border-red-200"],
};
const Badge = ({ s }) => <span className={`text-xs font-semibold border rounded-full px-2.5 py-0.5 whitespace-nowrap ${MTO_ST[s][1]}`}>{MTO_ST[s][0]}</span>;

const Tile = ({ label, value, sub, tone = "text-[#011E38]", testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-[0.7rem] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
    <div className={`num text-2xl font-bold mt-1.5 ${tone}`} data-testid={testid && `${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const orders = (c) => `${c} order${c === 1 ? "" : "s"}`;

export function FlowTiles({ f, out, prefix }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Tile label="MTO Placed" value={inr(f.placed.value)} sub={orders(f.placed.count)} testid={`${prefix}-placed`} />
      <Tile label="MTO Closed" value={inr(f.closed.value)} sub={orders(f.closed.count)} tone="text-green-700" testid={`${prefix}-closed`} />
      <Tile label="MTO Cancelled" value={inr(f.cancelled.value)} sub={orders(f.cancelled.count)} tone="text-red-700" testid={`${prefix}-cancelled`} />
      <Tile label="Outstanding (current)" value={inr(out.value)} sub={`${out.count} open`} tone="text-amber-700" testid={`${prefix}-outstanding`} />
    </div>
  );
}

export function MtoCounts({ s }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Tile label="Total MTO Placed" value={inr(s.lifetime.placed)} sub="all time" testid="mto-life-placed" />
      <Tile label="Open MTOs" value={num(s.counts.open)} sub={`${s.counts.partially_closed} partially closed`} testid="mto-count-open" />
      <Tile label="MTOs Closed" value={num(s.counts.completed)} sub={`${inr(s.lifetime.closed)} billed`} tone="text-green-700" testid="mto-count-closed" />
      <Tile label="MTOs Cancelled" value={num(s.counts.cancelled)} sub={`${inr(s.lifetime.cancelled)} cancelled`} tone="text-red-700" testid="mto-count-cancelled" />
    </div>
  );
}

export function MtoTable({ rows, onOpen, showStaff, testid = "mto-table" }) {
  if (!rows.length) return <Empty>No MTOs here.</Empty>;
  return (
    <div className="bs-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm text-left min-w-[640px]">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="px-4 py-3">Customer</th>{showStaff && <th className="px-3">Staff</th>}<th className="px-3 text-right">MTO</th><th className="px-3 text-right">Closed</th><th className="px-3 text-right">Cancelled</th><th className="px-3 text-right">Outstanding</th><th className="px-4">Status</th></tr>
        </thead>
        <tbody>{rows.map((m) => (
          <tr key={m.id} onClick={() => onOpen(m.id)} className="border-t border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`mto-row-${m.mto_ref}`}>
            <td className="px-4 py-3"><div className="font-semibold text-[#011E38]">{m.customer_name}</div><div className="text-xs text-slate-500">{m.mto_ref} · {fmtDate(m.date)}</div></td>
            {showStaff && <td className="px-3">{m.staff_name}</td>}
            <td className="px-3 text-right num">{inr(m.amount)}</td>
            <td className="px-3 text-right num text-green-700">{inr(m.closed)}</td>
            <td className="px-3 text-right num text-red-700">{inr(m.cancelled)}</td>
            <td className="px-3 text-right num font-bold">{inr(m.outstanding)}</td>
            <td className="px-4"><Badge s={m.status} /></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Adjust({ m, onDone }) {
  const { settings } = useAuth();
  const [f, setF] = useState({ closed: "", cancelled: "", date: settings.effective_date, remarks: "" });
  const save = async () => {
    try {
      await api.post(`/mto/${m.id}/adjust`, { ...f, closed: Number(f.closed) || 0, cancelled: Number(f.cancelled) || 0 });
      toast.success("Correction recorded");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="rounded-xl bg-slate-50 p-3 space-y-2" data-testid="mto-adjust">
      <div className="text-xs font-bold text-slate-600 uppercase">Manager correction</div>
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" placeholder="Closed ₹" value={f.closed} onChange={s("closed")} data-testid="mto-adjust-closed" />
        <Input type="number" placeholder="Cancelled ₹" value={f.cancelled} onChange={s("cancelled")} data-testid="mto-adjust-cancelled" />
        <Input type="date" max={settings.effective_date} value={f.date} onChange={s("date")} data-testid="mto-adjust-date" />
      </div>
      <Input placeholder="Reason (required)" value={f.remarks} onChange={s("remarks")} data-testid="mto-adjust-remarks" />
      <Button size="sm" disabled={!f.remarks.trim()} onClick={save} className="bg-[#011E38]" data-testid="mto-adjust-submit">Record correction</Button>
    </div>
  );
}

export function MtoHistory({ id, onClose, onChanged }) {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const load = () => api.get(`/mto/${id}`).then((r) => setM(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="mto-history">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">{m ? `${m.mto_ref} · ${m.customer_name}` : "Loading…"}</DialogTitle></DialogHeader>
        {m && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">{m.mobile} · {m.staff_name} <Badge s={m.status} /></div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Original", m.amount], ["Closed", m.closed], ["Cancelled", m.cancelled], ["Outstanding", m.outstanding]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-slate-50 py-2"><div className="text-[0.65rem] uppercase text-slate-500">{l}</div><div className="num font-bold text-[#011E38]">{inr(v)}</div></div>
              ))}
            </div>
            <div className="space-y-1.5" data-testid="mto-history-list">
              {m.history.map((h, i) => (
                <div key={i} className="flex justify-between text-sm border-b border-slate-100 py-1.5">
                  <span className="text-slate-500 w-24">{fmtDate(h.date)}</span>
                  <span className="flex-1">{h.type === "created" ? `Created ${inrFull(h.amount)}` : [h.closed > 0 && `Closed ${inrFull(h.closed)}`, h.cancelled > 0 && `Cancelled ${inrFull(h.cancelled)}`].filter(Boolean).join(" · ")}
                    {h.source === "manager_correction" && <span className="text-xs text-amber-700"> (correction{h.remarks && `: ${h.remarks}`})</span>}</span>
                  <span className="text-xs text-slate-400">{h.by}</span>
                </div>
              ))}
            </div>
            {user.role !== "staff" && m.outstanding > 0 && <Adjust m={m} onDone={() => { load(); onChanged?.(); }} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function MyMto({ range }) {
  const [s, setS] = useState(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/mto/summary", { params: { start: range.start, end: range.end } }), api.get("/mto/list", { params: { status: "open" } })])
      .then(([a, b]) => { setS(a.data); setRows(b.data); });
  }, [range]);
  if (!s) return null;
  return (
    <section className="space-y-4 mt-8" data-testid="my-mto">
      <SectionTitle eyebrow="Made to order" title="MY MTO" />
      <FlowTiles f={s.period} out={s.outstanding} prefix="my-mto" />
      <MtoCounts s={s} />
      <div className="bs-eyebrow pt-2">Open MTOs</div>
      <MtoTable rows={rows} onOpen={setOpen} testid="my-mto-open" />
      {open && <MtoHistory id={open} onClose={() => setOpen(null)} />}
    </section>
  );
}
